// glonet-react-tailwind-project--src--components--MicSelector.js
// Silent-mic rescue for the landing voice intake, modeled on the adventure
// game's MicCheck (src/components/AdventureGame/MicCheck.js) but invisible
// until it's needed: it meters the mic the whole time the session is live,
// and only when nothing has been heard for `silenceSecs` does it surface a
// short warning + device picker (BlackHole/Zoom/Meet loopback setups and
// Bluetooth headsets routinely leave the browser on a dead input). The
// moment real speech registers it disappears again.
import React, { useCallback, useEffect, useRef, useState } from "react";

export const GA_MIC_DEVICE_KEY = "ga_mic_device";

const ACCENT = "#74a0f5";
const MONO = "'JetBrains Mono',monospace";
const SILENCE_RMS = 0.012; // below this ≈ no speech energy (same as the game)

// live: session is up · silenceSecs: reveal threshold (admin-tunable)
// holdOff: the agent is speaking/thinking — the member is LISTENING, their
//   silence is natural, so the clock must not run (the eager-popup bug)
// heardTs: timestamp of the last user transcript — the agent PROVABLY heard
//   the member, which beats any local RMS guess: dismiss instantly, and don't
//   come back this session (a mic that worked once isn't the problem anymore)
export default function MicSelector({ live, silenceSecs = 5, onDeviceChange, holdOff = false, heardTs = 0 }) {
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(() => localStorage.getItem(GA_MIC_DEVICE_KEY) || "");
  const [level, setLevel] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const audioRef = useRef(null); // {ctx, analyser, stream, raf}
  const silentSinceRef = useRef(null);
  const warnMsRef = useRef(silenceSecs * 1000);
  warnMsRef.current = Math.max(2, silenceSecs) * 1000;
  const holdOffRef = useRef(holdOff);
  holdOffRef.current = holdOff;
  const everHeardRef = useRef(false);

  useEffect(() => {
    if (!heardTs) return;
    everHeardRef.current = true;
    silentSinceRef.current = null;
    setRevealed(false);
  }, [heardTs]);

  const stopMeter = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    cancelAnimationFrame(a.raf);
    try { a.stream.getTracks().forEach((t) => t.stop()); } catch (e) { /* gone */ }
    try { a.ctx.close(); } catch (e) { /* gone */ }
    audioRef.current = null;
  }, []);

  const startMeter = useCallback(async (devId) => {
    stopMeter();
    setRevealed(false);
    silentSinceRef.current = null;
    try {
      const constraints = { audio: devId ? { deviceId: { exact: devId } } : true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      const a = { ctx, analyser, stream, raf: 0 };
      audioRef.current = a;

      const tick = () => {
        if (audioRef.current !== a) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        setLevel(rms);
        const now = Date.now();
        if (holdOffRef.current || everHeardRef.current) {
          // agent talking/thinking = member listening, not ignored; and a mic
          // the agent has already heard this session isn't the problem — the
          // clock only runs while the agent is waiting on an unheard member
          silentSinceRef.current = null;
        } else if (rms < SILENCE_RMS) {
          if (silentSinceRef.current == null) silentSinceRef.current = now;
          if (now - silentSinceRef.current > warnMsRef.current) setRevealed(true);
        } else {
          silentSinceRef.current = null;
          setRevealed(false); // heard you locally — get out of the way
        }
        a.raf = requestAnimationFrame(tick);
      };
      tick();

      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices(list.filter((d) => d.kind === "audioinput"));
    } catch (e) {
      // stale persisted device: retry on the default mic
      if (devId) {
        localStorage.removeItem(GA_MIC_DEVICE_KEY);
        setDeviceId("");
        startMeter("");
      }
    }
  }, [stopMeter]);

  useEffect(() => {
    if (live) startMeter(deviceId);
    else stopMeter();
    return stopMeter;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  const pickDevice = async (id) => {
    setDeviceId(id);
    localStorage.setItem(GA_MIC_DEVICE_KEY, id);
    if (live) await startMeter(id);
    if (onDeviceChange) onDeviceChange(id);
  };

  if (!live || !revealed) return null;

  const bars = 12;
  const lit = Math.min(bars, Math.round((level / 0.25) * bars));
  const cur = devices.find((d) => d.deviceId === deviceId);
  const isLoopback = cur && /blackhole|loopback|aggregate|soundflower|display audio/i.test(cur.label || "");

  return (
    <div data-testid="ga-mic-selector"
         style={{ marginTop: 14, padding: "12px 16px", borderRadius: 12, textAlign: "left",
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(224,163,94,0.45)" }}>
      <div style={{ fontSize: 13.5, lineHeight: 1.5, color: "#e0a35e", marginBottom: 10 }}>
        We can&apos;t hear you — pick another mic (Bluetooth and loopback devices often
        fail to engage), or check the mic icon in the address bar.
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div title="Your voice, as the agent hears it"
             style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 18 }}>
          {[...Array(bars)].map((_, i) => (
            <div key={i} style={{
              width: 3, borderRadius: 1, height: 5 + i * 1.1,
              background: i < lit ? ACCENT : "rgba(255,255,255,0.12)",
              transition: "background 80ms",
            }} />
          ))}
        </div>
        <select value={deviceId} onChange={(e) => pickDevice(e.target.value)}
                style={{ fontFamily: MONO, fontSize: 12, color: "#f1f2f4", background: "#0c0d10",
                         border: "1px solid rgba(255,255,255,0.18)", borderRadius: 8,
                         padding: "6px 8px", maxWidth: 240 }}>
          <option value="">Default microphone</option>
          {devices.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>{d.label || `Mic ${d.deviceId.slice(0, 6)}`}</option>
          ))}
        </select>
        {isLoopback && (
          <span style={{ fontSize: 12.5, color: "#e0a35e", fontStyle: "italic", maxWidth: 300 }}>
            That device echoes system audio — pick a real microphone.
          </span>
        )}
      </div>
    </div>
  );
}
